/**
 * WorkshopsTable — the "Workshops here" table on the venue Workshops tab
 * (BON-168 / BON-197). One row per workshop at this venue for the active
 * `Upcoming | Past | All` direction: date chip + time, workshop type (tree
 * species), teacher + assistant (avatar + name from `crew`), a fill bar with
 * booked/capacity, and a Status cell.
 *
 * Status cell (two axes, Round 1): an active + FUTURE row shows the PACING pill
 * (Axis B — On track / Behind Pace / Sold out, matching the mock); every other
 * row (past, draft, cancelled) shows the lifecycle STATUS (Axis A — Completed /
 * Cancelled / Draft). Pacing vocabulary never appears on a past row — sold-out
 * past workshops are read from the Fill column.
 *
 * Rows link to the workshop detail page in the tree the table is mounted in.
 * In the admin tree a private event row is badged and opens the private event
 * page instead, carrying this venue as the place to come back to.
 */
import { useLocation } from 'react-router-dom';

import type { Workshop, WorkshopDirection } from '../../../../api/workshops';
import { privateEventReturnState } from '../../../private-events/private-event-return';
import { chipDetailUrlFor, workshopShellFromPathname } from '../../workshopDetailUrlFor';

import { WorkshopsTableRow } from './WorkshopsTableRow';

/** Empty-state copy per direction (matches the toggle the user selected). */
const EMPTY_COPY: Record<WorkshopDirection, string> = {
  upcoming: 'No upcoming workshops scheduled here.',
  past: 'No past workshops here.',
  all: 'No workshops here.',
};

export function WorkshopsTable({
  workshops,
  direction,
}: {
  workshops: Workshop[];
  direction: WorkshopDirection;
}) {
  const { pathname } = useLocation();
  const shell = workshopShellFromPathname(pathname);
  // Single `now` for the whole table so every row resolves against the same
  // past/future boundary (mirrors the server's single-`now` contract).
  const now = new Date();
  if (workshops.length === 0) {
    return (
      <div style={{ fontSize: 12, color: 'var(--nb-subtle)', padding: '8px 0' }}>
        {EMPTY_COPY[direction]}
      </div>
    );
  }
  return (
    <table className="nb-tbl">
      <thead>
        <tr>
          <th>Date</th>
          <th>Workshop Type</th>
          <th>Teacher</th>
          <th>Assistant</th>
          <th>Fill</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {workshops.map((w) => (
          <WorkshopsTableRow
            key={w.id}
            w={w}
            detailHref={chipDetailUrlFor(shell, w)}
            returnState={privateEventReturnState(pathname, w.venueName || 'Venue')}
            now={now}
          />
        ))}
      </tbody>
    </table>
  );
}
